import React, { useState, useMemo } from "react";
import { requestConsent } from "../../Services/services";
import { useToast } from "./ToastContainer";

const HI_TYPES = [
  "OPConsultation",
  "Prescription",
  "DiagnosticReport",
  "WellnessRecord",
  "DischargeSummary",
  "HealthDocumentRecord",
  "ImmunizationRecord",
];

const EXPIRY_OPTIONS = ["1 Week", "15 Days", "30 Days", "3 Months", "1 Year"];
const ABHA_ADDRESS_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9._-]{2,}@[a-zA-Z0-9._-]+$/;

function ConsentRequest({ onSuccess, offcanvasId = "offcanvasRight" }) {
  const { showSuccess, showError } = useToast();

  const [abhaId, setAbhaId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [fromTime, setFromTime] = useState("");
  const [toDate, setToDate] = useState("");
  const [toTime, setToTime] = useState("");
  const [hiTypes, setHiTypes] = useState([]);
  const [expiryDate, setExpiryDate] = useState("");
  const [expiryTime, setExpiryTime] = useState("");
  const [expiryOption, setExpiryOption] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isValidatingAbha, setIsValidatingAbha] = useState(false);
  const [abhaValidationStatus, setAbhaValidationStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const showToast = (message, type = "success") => {
    if (type === "success") showSuccess(message);
    else showError(message);
  };

  const resetForm = () => {
    setCurrentStep(1);
    setAbhaId("");
    setAbhaValidationStatus(null);
    setPatientData(null);
    setFromDate("");
    setFromTime("");
    setToDate("");
    setToTime("");
    setHiTypes([]);
    setExpiryDate("");
    setExpiryTime("");
    setExpiryOption("");
    setSuccessMessage("");
    setFormError("");
  };

  const closeOffcanvas = () => {
    const el = document.getElementById(offcanvasId);
    if (el && window.bootstrap?.Offcanvas) {
      const instance =
        window.bootstrap.Offcanvas.getInstance(el) ||
        new window.bootstrap.Offcanvas(el);
      instance.hide();
    }
  };

  const validateAbhaId = async () => {
    const normalizedAbhaId = abhaId.trim().toLowerCase();
    if (!ABHA_ADDRESS_REGEX.test(normalizedAbhaId)) {
      showToast("Enter a valid ABHA address (e.g. name@sbx or name@abdm).", "error");
      return;
    }
    setAbhaId(normalizedAbhaId);
    setIsValidatingAbha(true);
    setAbhaValidationStatus(null);
    try {
      // const tokenResponse = await fetch("http://localhost:4200/abdm/api/abha/token", {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      // });
      // if (!tokenResponse.ok) throw new Error("Failed to get access token");
      // const tokenData = await tokenResponse.json();
      // const accessToken = tokenData.accessToken || tokenData.token;
      // if (!accessToken) throw new Error("Access token not received");

      const searchResponse = await fetch(
        "https://bhims.pranamm.ai/abdm/api/abha/profile/search",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            // accesstoken: accessToken,
          },
          body: JSON.stringify({ address: normalizedAbhaId }),
        },
      );
      if (!searchResponse.ok) throw new Error("Address not found");
      const searchData = await searchResponse.json();

      if (searchData.success && searchData.data) {
        setAbhaValidationStatus("valid");
        setPatientData(searchData.data);
        showToast(
          `ABHA ID verified successfully! Name: ${searchData.data.fullName}`,
          "success",
        );
        setTimeout(() => setCurrentStep(2), 500);
      } else {
        setAbhaValidationStatus("invalid");
        setPatientData(null);
        showToast("Address not found", "error");
      }
    } catch (error) {
      console.error("ABHA Validation Error:", error);
      setAbhaValidationStatus("invalid");
      setPatientData(null);
      showToast(error.message || "Address not found", "error");
    } finally {
      setIsValidatingAbha(false);
    }
  };

  const handleCheckbox = (type) => {
    setHiTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const handleSelectAllHiTypes = () => {
    if (hiTypes.length === HI_TYPES.length) {
      setHiTypes([]);
    } else {
      setHiTypes([...HI_TYPES]);
    }
  };

  const handleExpiryOption = (option) => {
    setExpiryOption(option);
    const today = new Date();
    switch (option) {
      case "1 Week":
        today.setDate(today.getDate() + 7);
        break;
      case "15 Days":
        today.setDate(today.getDate() + 15);
        break;
      case "30 Days":
        today.setDate(today.getDate() + 30);
        break;
      case "3 Months":
        today.setMonth(today.getMonth() + 3);
        break;
      case "1 Year":
        today.setFullYear(today.getFullYear() + 1);
        break;
      default:
        break;
    }
    setExpiryDate(today.toISOString().split("T")[0]);
  };

  const handleToTimeChange = (value) => {
    const today = new Date().toISOString().split("T")[0];
    if (toDate === today) {
      const now = new Date();
      now.setMinutes(now.getMinutes() - 1);
      const maxTime = now.toTimeString().slice(0, 5);

      if (value > maxTime) {
        showToast(
          "Cannot select time greater than current time - 1 minute",
          "error",
        );
        return;
      }
    }
    setToTime(value);
  };

  const isFormValid = useMemo(() => {
    const normalizedAbhaId = abhaId.trim().toLowerCase();
    const hasValidAbha =
      normalizedAbhaId !== "" &&
      ABHA_ADDRESS_REGEX.test(normalizedAbhaId) &&
      abhaValidationStatus === "valid";
    const hasFromDate = fromDate !== "";
    const hasToDate = toDate !== "";
    const effectiveFromTime = fromTime || "00:00";
    const todayStr = new Date().toISOString().split("T")[0];
    const effectiveToTime =
      toTime ||
      (toDate === todayStr
        ? (() => {
            const now = new Date();
            now.setMinutes(now.getMinutes() - 1);
            return now.toTimeString().slice(0, 5);
          })()
        : "23:59");
    const fromDateTime = fromDate ? new Date(`${fromDate}T${effectiveFromTime}`) : null;
    const toDateTime = toDate ? new Date(`${toDate}T${effectiveToTime}`) : null;
    const expiryDateTime = expiryDate ? new Date(`${expiryDate}T${expiryTime || "23:59"}`) : null;
    const hasValidDates =
      hasFromDate &&
      hasToDate &&
      fromDateTime &&
      toDateTime &&
      !Number.isNaN(fromDateTime.getTime()) &&
      !Number.isNaN(toDateTime.getTime()) &&
      toDateTime >= fromDateTime;
    const hasHiTypes = hiTypes.length > 0;
    const hasExpiry =
      (expiryDate !== "" || expiryOption !== "") &&
      (!expiryDateTime || expiryDateTime > new Date());
    return (
      hasValidAbha &&
      hasValidDates &&
      hasHiTypes &&
      hasExpiry
    );
  }, [
    abhaId,
    fromDate,
    fromTime,
    toDate,
    toTime,
    hiTypes,
    expiryDate,
    expiryTime,
    expiryOption,
    abhaValidationStatus,
  ]);

  const validationError = useMemo(() => {
    const normalizedAbhaId = abhaId.trim().toLowerCase();
    if (!normalizedAbhaId || !ABHA_ADDRESS_REGEX.test(normalizedAbhaId)) {
      return "Please enter a valid ABHA address (e.g. name@sbx or name@abdm).";
    }
    if (abhaValidationStatus !== "valid") {
      return "ABHA address must be verified first.";
    }
    if (!fromDate) {
      return "Please select 'Health info from' date.";
    }
    if (!toDate) {
      return "Please select 'Health info To' date.";
    }
    const effectiveFromTime = fromTime || "00:00";
    const todayStr = new Date().toISOString().split("T")[0];
    const effectiveToTime =
      toTime ||
      (toDate === todayStr
        ? (() => {
            const now = new Date();
            now.setMinutes(now.getMinutes() - 1);
            return now.toTimeString().slice(0, 5);
          })()
        : "23:59");
    const fromDateTime = new Date(`${fromDate}T${effectiveFromTime}`);
    const toDateTime = new Date(`${toDate}T${effectiveToTime}`);
    if (
      Number.isNaN(fromDateTime.getTime()) ||
      Number.isNaN(toDateTime.getTime()) ||
      toDateTime < fromDateTime
    ) {
      return "'Health info To' date/time must be after 'Health info from' date/time.";
    }
    if (hiTypes.length === 0) {
      return "Please select at least one Health info Type.";
    }
    const expiryDateTime = expiryDate ? new Date(`${expiryDate}T${expiryTime || "23:59"}`) : null;
    if (!expiryDate && !expiryOption) {
      return "Please select Health info Expiry date or duration.";
    }
    if (expiryDateTime && expiryDateTime <= new Date()) {
      return "Health info Expiry must be a date/time in the future.";
    }
    return null;
  }, [
    abhaId,
    abhaValidationStatus,
    fromDate,
    fromTime,
    toDate,
    toTime,
    hiTypes,
    expiryDate,
    expiryTime,
    expiryOption,
  ]);

  const handleClick = async () => {
    if (!isFormValid) {
      setFormError(validationError || "Verify the ABHA address, choose at least one record type, and enter a valid date/time range and future expiry.");
      return;
    }
    setFormError("");
    setLoading(true);
    setSuccessMessage("");
    try {
      let expiryDayStr = expiryDate;
      if (!expiryDayStr && expiryOption) {
        const today = new Date();
        switch (expiryOption) {
          case "1 Week":
            today.setDate(today.getDate() + 7);
            break;
          case "15 Days":
            today.setDate(today.getDate() + 15);
            break;
          case "30 Days":
            today.setDate(today.getDate() + 30);
            break;
          case "3 Months":
            today.setMonth(today.getMonth() + 3);
            break;
          case "1 Year":
            today.setFullYear(today.getFullYear() + 1);
            break;
          default:
            break;
        }
        expiryDayStr = today.toISOString().split("T")[0];
      }

      const ensureTimeSeconds = (timeStr, defaultTime = "00:00:00") => {
        if (!timeStr) return defaultTime;
        const parts = timeStr.split(":");
        if (parts.length === 2) return `${timeStr}:00`;
        return timeStr;
      };

      const effectiveFromTime = ensureTimeSeconds(fromTime, "00:00:00");
      let effectiveToTime = ensureTimeSeconds(toTime, "");
      if (!effectiveToTime) {
        const todayStr = new Date().toISOString().split("T")[0];
        if (toDate === todayStr) {
          const now = new Date();
          now.setMinutes(now.getMinutes() - 1);
          effectiveToTime = ensureTimeSeconds(now.toTimeString().slice(0, 5), "00:00:00");
        } else {
          effectiveToTime = "23:59:59";
        }
      }

      const fromDateTime = new Date(`${fromDate}T${effectiveFromTime}`).toISOString();
      const toDateTime = new Date(`${toDate}T${effectiveToTime}`).toISOString();

      const effectiveExpiryTime = ensureTimeSeconds(expiryTime, "23:59:59");
      const finalExpiryTime = new Date(`${expiryDayStr}T${effectiveExpiryTime}`).toISOString();

      const formData = {
        abha_id: abhaId.trim().toLowerCase(),
        facilityId: "IN0710001891",
        hiTypes,
        from: fromDateTime,
        to: toDateTime,
        dataEraseAt: finalExpiryTime,
        patientData: patientData,
      };

      const result = await requestConsent(formData);
      if (result?.message) {
        showToast(result.message, "success");
      } else {
        showToast("Consent request submitted successfully!", "success");
      }
      setSuccessMessage("Consent request sent successfully!");

      if (typeof onSuccess === "function") {
        setTimeout(onSuccess, 1000);
      }
      setTimeout(() => {
        resetForm();
        closeOffcanvas();
      }, 2000);
    } catch (error) {
      console.error(error);
      showToast(
        error.message || "An error occurred while processing your request.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    closeOffcanvas();
  };

  return (
    <div
      className="consentForms offcanvas offcanvas-end"
      tabIndex={-1}
      id={offcanvasId}
      aria-labelledby="offcanvasRightLabel"
    >
      <div className="offcanvas-header">
        <h5 id="offcanvasRightLabel">ADD NEW CONSENTS</h5>
        <button
          type="button"
          className="btn-close text-reset"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={handleCancel}
        />
      </div>
      <div className="offcanvas-body">
        {currentStep === 1 && (
          <div className="addNewConsent">
            <div className="feildsRow">
              <div className="feilds">
                <p>Patients ABHA Address</p>
                <input
                  type="text"
                  placeholder="e.g. name@sbx or name@abdm"
                  value={abhaId}
                  onChange={(e) => {
                    setAbhaId(e.target.value);
                    setAbhaValidationStatus(null);
                    setPatientData(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && abhaId.trim() && !isValidatingAbha) {
                      e.preventDefault();
                      validateAbhaId();
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }}
                />
              </div>
            </div>
            <div style={{ marginTop: "20px" }}>
              <button
                type="button"
                onClick={validateAbhaId}
                disabled={!abhaId.trim() || isValidatingAbha}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor:
                    !abhaId.trim() || isValidatingAbha
                      ? "not-allowed"
                      : "pointer",
                  opacity: !abhaId.trim() || isValidatingAbha ? 0.6 : 1,
                  fontSize: "16px",
                  fontWeight: "500",
                }}
              >
                {isValidatingAbha ? "Searching..." : "Search Patients"}
              </button>
            </div>
            {abhaValidationStatus === "invalid" && (
              <div
                style={{
                  marginTop: "15px",
                  padding: "10px",
                  backgroundColor: "#f8d7da",
                  color: "#721c24",
                  borderRadius: "4px",
                }}
              >
                ✗ Patient not found. Please check the ABHA address and try
                again.
              </div>
            )}
          </div>
        )}

        {currentStep === 2 && patientData && (
          <div className="addNewConsent">
            <div className="feildsRow">
              <div className="feilds">
                <p>Patients ABHA Address</p>
                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  <input
                    type="text"
                    value={abhaId}
                    disabled
                    style={{
                      flex: 1,
                      padding: "10px",
                      borderRadius: "4px",
                      border: "1px solid #ddd",
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                  <button
                    onClick={() => setCurrentStep(1)}
                    style={{
                      padding: "10px 16px",
                      backgroundColor: "rgb(0, 123, 255)",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: "20px",
                padding: "15px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <h6 style={{ margin: 0, fontWeight: "bold" }}>
                    {patientData.fullName}
                  </h6>
                  <p
                    style={{
                      margin: "5px 0 0 0",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    {patientData.status} | {patientData.mobile}
                  </p>
                </div>
                <button
                  onClick={() => setCurrentStep(3)}
                  style={{
                    padding: "8px 20px",
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <span>✓</span> Select
                </button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="addNewConsent" style={{ position: "relative" }}>
            {formError && (
              <div role="alert" style={{ marginBottom: 12, color: "#721c24", backgroundColor: "#f8d7da", padding: 10, borderRadius: 4 }}>
                {formError}
              </div>
            )}
            {successMessage && (
              <div
                style={{
                  position: "absolute",
                  top: "-60px",
                  right: 0,
                  padding: "12px 20px",
                  backgroundColor: "#d4edda",
                  color: "#155724",
                  border: "1px solid #c3e6cb",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  zIndex: 1000,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <span style={{ fontSize: "18px" }}>✓</span>
                <span style={{ fontWeight: "500" }}>
                  Consent Has been requested
                </span>
              </div>
            )}

            <div className="feildsRow">
              <div className="feilds">
                <p>Patients ABHA Address</p>
                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  <input
                    type="text"
                    value={abhaId}
                    disabled
                    style={{
                      flex: 1,
                      padding: "10px",
                      borderRadius: "4px",
                      border: "1px solid #ddd",
                      backgroundColor: "#f5f5f5",
                      cursor: "not-allowed",
                    }}
                  />
                  <button
                    onClick={() => setCurrentStep(1)}
                    style={{
                      padding: "10px 16px",
                      backgroundColor: "rgb(0, 123, 255)",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: "15px",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                backgroundColor: "#f9f9f9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h6 style={{ margin: 0, fontWeight: "bold", fontSize: "15px" }}>
                  {patientData?.fullName || "Patient Name"}
                </h6>
                <p
                  style={{
                    margin: "3px 0 0 0",
                    fontSize: "13px",
                    color: "#666",
                  }}
                >
                  {patientData?.status} | {patientData?.mobile}
                </p>
              </div>
              <span
                role="status"
                style={{
                  padding: "6px 16px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <span>✓</span> Selected
              </span>
            </div>

            <div className="feildsRow" style={{ marginTop: "20px" }}>
              <div className="feilds">
                <p>Purpose of request</p>
                <select
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }}
                >
                  <option>Care Management</option>
                </select>
              </div>
            </div>

            <div className="feildsRow" style={{ display: "block" }}>
              <div className="feilds" style={{ marginBottom: "15px" }}>
                <p style={{ marginBottom: "8px" }}>Health info from</p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Date
                    </label>
                    <input
                      type="date"
                      value={fromDate}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => {
                        const selectedFromDate = e.target.value;
                        setFromDate(selectedFromDate);
                        if (!fromTime) setFromTime("00:00");
                        if (toDate && selectedFromDate > toDate) setToDate("");
                      }}
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Time
                    </label>
                    <input
                      type="time"
                      value={fromTime}
                      onChange={(e) => setFromTime(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="feilds">
                <p style={{ marginBottom: "8px" }}>Health info To</p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Date
                    </label>
                    <input
                      type="date"
                      value={toDate}
                      min={fromDate || ""}
                      max={new Date().toISOString().split("T")[0]}
                      disabled={!fromDate}
                      onChange={(e) => {
                        const selectedToDate = e.target.value;
                        setToDate(selectedToDate);
                        if (!toTime) {
                          const todayStr = new Date().toISOString().split("T")[0];
                          if (selectedToDate === todayStr) {
                            const now = new Date();
                            now.setMinutes(now.getMinutes() - 1);
                            setToTime(now.toTimeString().slice(0, 5));
                          } else {
                            setToTime("23:59");
                          }
                        }
                      }}
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                        backgroundColor: !fromDate ? "#f5f5f5" : "white",
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Time
                    </label>
                    <input
                      type="time"
                      value={toTime}
                      onChange={(e) => handleToTimeChange(e.target.value)}
                      disabled={!toDate}
                      max={
                        toDate === new Date().toISOString().split("T")[0]
                          ? (() => {
                              const now = new Date();
                              now.setMinutes(now.getMinutes() - 1);
                              return now.toTimeString().slice(0, 5);
                            })()
                          : undefined
                      }
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                        backgroundColor: !toDate ? "#f5f5f5" : "white",
                      }}
                      required
                    />
                  </div>
                </div>
                {!fromDate && (
                  <small
                    className="text-muted"
                    style={{ display: "block", marginTop: "6px" }}
                  >
                    Please select "Health info from" date first
                  </small>
                )}
              </div>
            </div>

            <div className="feildsRow feildsRowList">
              <div className="feilds">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <p style={{ margin: 0 }}>Health info Type</p>
                  <button
                    type="button"
                    onClick={handleSelectAllHiTypes}
                    style={{
                      padding: "3px 12px",
                      backgroundColor:
                        hiTypes.length === HI_TYPES.length
                          ? "#28a745"
                          : "#6c757d",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "500",
                    }}
                  >
                    {hiTypes.length === HI_TYPES.length
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                </div>
                <div className="list">
                  {HI_TYPES.map((type) => (
                    <label key={type}>
                      <input
                        type="checkbox"
                        checked={hiTypes.includes(type)}
                        onChange={() => handleCheckbox(type)}
                      />
                      <span>{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="feildsRow">
              <div className="feilds">
                <p style={{ marginBottom: "8px" }}>Health info Expiry</p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                    marginBottom: "10px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Date
                    </label>
                    <input
                      type="date"
                      value={expiryDate}
                      min={new Date().toISOString().split("T")[0]}
                      onChange={(e) => {
                        setExpiryDate(e.target.value);
                        setExpiryOption("");
                      }}
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Time
                    </label>
                    <input
                      type="time"
                      value={expiryTime}
                      onChange={(e) => setExpiryTime(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                </div>
                {expiryOption && (
                  <small
                    className="text-muted"
                    style={{ display: "block", marginBottom: "10px" }}
                  >
                    Expiry period selected: {expiryOption}
                  </small>
                )}
              </div>
            </div>

            <div className="feildsRow feildsRadioList">
              <div className="feilds">
                <div className="listRadio">
                  {EXPIRY_OPTIONS.map((opt) => (
                    <label key={opt}>
                      <input
                        type="radio"
                        name="expiry"
                        checked={expiryOption === opt}
                        onChange={() => handleExpiryOption(opt)}
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="offcanvas-footer">
        {currentStep === 3 && (
          <div
            className="buttonsectionConsent"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              width: "100%",
            }}
          >
            {!isFormValid && validationError && (
              <small
                style={{
                  color: "#dc3545",
                  fontSize: "12px",
                  textAlign: "right",
                  lineHeight: "1.3",
                }}
              >
                {validationError}
              </small>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "transparent",
                  color: "#666",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleClick}
                disabled={!isFormValid || loading}
                className="requestConsent"
                title={!isFormValid ? validationError : ""}
                style={{
                  padding: "10px 20px",
                  backgroundColor:
                    isFormValid && !loading ? "#007bff" : "#6c757d",
                  color: "white",
                  opacity: isFormValid && !loading ? 1 : 0.6,
                  cursor: isFormValid && !loading ? "pointer" : "not-allowed",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {loading ? "Requesting..." : "Request Consent"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConsentRequest;
